import { useEffect } from "react";
import { useMatches } from "react-router";
import { useUserStore } from "~/lib/stores/user";
import { devLog } from "~/lib/dev-log";
import { signInWithGoogleIdToken } from "~/lib/social-login";
import { isGuestUser } from "../user";

// Routes that render the deal-detail page (pages/deal/detail.tsx). On mobile that page fixes its
// "Buy now" bar to the bottom of the viewport, exactly where One Tap draws its bottom sheet — so One
// Tap would bury the primary CTA there. Matched by stable route id, not path, so a path rename is safe.
const DEAL_DETAIL_ROUTE_IDS = new Set(["deal-detail", "shop-deal-detail", "deal-info-legacy"]);

const GSI_SRC = "https://accounts.google.com/gsi/client";

/**
 * Load the Google Identity Services script and call `onReady` once it is available. Kept out of the
 * document <head> so its 99 KB does not compete with the product image at first paint on ad landings
 * (WEB-86); the single caller below defers this to browser idle. Initialization rides the script's
 * load event, so a blocked or failed script simply never calls back — nothing polls or retries.
 */
function loadGoogleIdentityScript(onReady: () => void) {
  if (typeof document === "undefined") {
    return;
  }
  if (window.google?.accounts?.id) {
    onReady();
    return;
  }
  const script = document.createElement("script");
  script.src = GSI_SRC;
  script.async = true;
  script.defer = true;
  script.addEventListener("load", onReady, { once: true });
  document.head.appendChild(script);
}

async function onOneTapCredential(response: any) {
  devLog("Google One Tap response:", response);
  if (!response.credential) {
    return;
  }
  try {
    const result = await signInWithGoogleIdToken(response.credential);
    // "google_one_tap" tags this login as One Tap — a distinct method value for analytics.
    await useUserStore.getState().firebaseLogin(result.idToken, result.email, "google_one_tap");
    devLog("✅ Google One Tap login successful");
  } catch (error) {
    console.error("❌ Google One Tap login failed:", error);
  }
}

function initAndPromptOneTap(clientId: string) {
  if (!window.google?.accounts?.id) {
    return;
  }
  // The idle delay means the user may have signed in since start; read guest status now, at the
  // moment it matters, rather than prompting a user who is no longer signed out.
  if (!isGuestUser(useUserStore.getState().user)) {
    return;
  }
  try {
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: onOneTapCredential,
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    window.google.accounts.id.prompt((notification: any) => {
      if (notification.isNotDisplayed()) {
        devLog("Google One Tap not displayed:", notification.getNotDisplayedReason());
        // Common reasons: browser_not_supported, suppressed_by_user (cooling off), opt_out_or_no_session
      } else if (notification.isSkippedMoment()) {
        devLog("Google One Tap skipped:", notification.getSkippedReason());
      } else if (notification.isDismissedMoment()) {
        devLog("Google One Tap dismissed:", notification.getDismissedReason());
      }
    });
  } catch (error) {
    console.error("Error initializing Google One Tap:", error);
  }
}

// One Tap's script load and prompt are page-global side effects. This guard runs them a single time,
// so a Header that remounts on every route change cannot re-inject the script or re-prompt.
let oneTapStarted = false;

function startOneTapOnce() {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (oneTapStarted || !clientId) {
    return;
  }
  oneTapStarted = true;
  // Defer the download off first paint; requestIdleCallback is absent in Safari, hence the fallback.
  const load = () => loadGoogleIdentityScript(() => initAndPromptOneTap(clientId));
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(load, { timeout: 3000 });
  } else {
    window.setTimeout(load, 2000);
  }
}

/**
 * Show Google One Tap to a guest. The load/init/prompt run once as a page-global side effect; this
 * hook only gates that on guest status, so it keeps no per-mount timers or listeners and is safe on
 * a Header that remounts on every route change.
 */
export function useGoogleOneTap() {
  const isGuest = useUserStore(s => isGuestUser(s.user));
  const onDealDetail = useMatches().some(m => DEAL_DETAIL_ROUTE_IDS.has(m.id));

  useEffect(() => {
    // Suppress One Tap on the deal-detail page so its bottom sheet never covers the fixed "Buy now"
    // bar. Also close a prompt still open from a previous page as the user navigates in here.
    if (onDealDetail) {
      window.google?.accounts?.id?.cancel();
      return;
    }
    if (isGuest) {
      startOneTapOnce();
    }
  }, [isGuest, onDealDetail]);
}
